import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openPortal } from '../../services/portal';
import { GlyphButton } from './Header';

/** Person icon: head and shoulders (16px, stroke inherits the text colour). */
export function AccountIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

/** The header's account glyph: a small menu of portal pages, each opened in the browser already signed in
 *  (services/portal.ts `openPortal`). Billing and invoices are an admin's pages on the portal, so members do not
 *  see them here. */
export function AccountMenu({ admin, email }: { admin: boolean; email?: string | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const items: { path: string; label: string }[] = [
    { path: '/usage', label: t('account.usage') },
    ...(admin ? [{ path: '/billing', label: t('account.billing') }, { path: '/invoices', label: t('account.invoices') }] : []),
    { path: '/organization', label: t('account.organization') },
    { path: '/', label: t('account.profile') },
  ];

  return (
    <div ref={root} className="relative">
      <GlyphButton glyph={<AccountIcon />} title={t('account.menu')} onClick={() => setOpen((o) => !o)} active={open} />
      {open && (
        <div role="menu" className="absolute right-0 top-8 z-30 min-w-[190px] rounded-md border border-line bg-white py-1 shadow-lg" data-testid="account-menu">
          {email && <div className="truncate border-b border-line-soft px-3 pb-1.5 pt-1 font-mono text-[11px] text-faint">{email}</div>}
          {items.map((item) => (
            <button
              key={item.path}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                void openPortal(item.path);
              }}
              className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-[12.5px] text-ink hover:bg-hover"
            >
              {item.label}
              <span className="text-faint" aria-hidden="true">↗</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
